// Admin-only escalation policies list. Same layout pattern as Services
// — top-of-page action button opens an inline create card; rows link to
// the detail/edit page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { Plus, Workflow, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { orgEscalationPoliciesCol } from '../lib/firestore';
import { createEscalationPolicy, readPolicy } from '../lib/escalations';
import type { EscalationPolicy } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
  Table,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function EscalationPoliciesPage() {
  const { user } = useAuth();
  const { orgId } = useOrg();

  const [policies, setPolicies] = useState<EscalationPolicy[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inline create — name only at this stage. Detail page handles step
  // editing once the policy doc exists.
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      query(orgEscalationPoliciesCol(orgId), orderBy('name', 'asc')),
      (snap) => {
        setPolicies(
          snap.docs.map((d) => readPolicy(d.data() as Parameters<typeof readPolicy>[0])),
        );
      },
      (err) => {
        console.error('[escalation] snapshot failed', err);
        setError('Could not load escalation policies.');
      },
    );
    return () => unsub();
  }, [orgId]);

  if (!orgId || !user) return null;

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      // Seed the new policy with a single step targeting the creator —
      // gives them a runnable starting point, easier than enforcing
      // empty-step validation in the editor.
      await createEscalationPolicy(orgId!, {
        name: newName.trim(),
        steps: [
          {
            delayMinutes: 0,
            targets: [{ type: 'user', id: user!.uid }],
          },
        ],
      });
      setNewName('');
      setShowForm(false);
    } catch (err) {
      console.error('[escalation] create failed', err);
      setError(extractMessage(err));
    } finally {
      setCreating(false);
    }
  }

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="Escalation policies"
          subtitle="Define who gets notified when a service incident is raised — and in what order."
          actions={
            !showForm ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowForm(true)}
                iconLeft={<Plus size={14} strokeWidth={2} />}
              >
                Add policy
              </Button>
            ) : null
          }
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          {showForm && (
            <Card>
              <CardHeader>
                <Row gap={2} align="center">
                  <Workflow size={16} strokeWidth={1.75} color="var(--color-accent)" />
                  <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                    New policy
                  </h2>
                </Row>
              </CardHeader>
              <CardBody>
                <Stack gap={4} as="form" onSubmit={submitCreate}>
                  <FormField
                    label="Name"
                    hint="You'll define the steps next. We'll seed it with a single step targeting you."
                  >
                    {(id) => (
                      <Input
                        id={id}
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Default policy"
                        required
                        maxLength={100}
                        autoFocus
                      />
                    )}
                  </FormField>
                  <Row gap={2} justify="end">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowForm(false)}
                      disabled={creating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!newName.trim()}
                      loading={creating}
                    >
                      Create policy
                    </Button>
                  </Row>
                </Stack>
              </CardBody>
            </Card>
          )}

          {policies === null ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          ) : policies.length === 0 ? (
            <Card>
              <CardBody>
                <p className="ds-text-muted">
                  No escalation policies yet. Create one above — it&apos;ll be available to attach
                  to services in the next chunk.
                </p>
              </CardBody>
            </Card>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Steps</th>
                  <th>Updated</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span style={{ fontWeight: 'var(--fw-medium)' }}>{p.name}</span>
                    </td>
                    <td>
                      <Badge tone="neutral">{p.steps.length}</Badge>
                    </td>
                    <td className="ds-table__mono">{formatDate(p.updatedAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Link
                        to={`/escalation-policies/${p.id}`}
                        className="ds-button ds-button--ghost ds-button--sm"
                      >
                        Manage
                        <ArrowRight size={12} strokeWidth={2} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Stack>
      </Container>
    </AppShell>
  );
}

function formatDate(ts: number): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}

